import {
  Row,
  Col,
  Button,
  message,
  Table,
  Tag,
  Input,
  Modal,
  Descriptions,
  Alert,
} from "antd";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  approveSocietyAmendment,
  cancelSocietyAmendment,
  getAmendmentById,
  rejectSocietyAmendment,
} from "store/admin-slice/adminServicesSlice";
import { routes } from "common/routes";
import { getPersistedUser } from "services/PersistService/PersistUserService";

const MESSAGE_KEY = "society_amendment_detail";

const statusColorMap: Record<string, string> = {
  PENDING: "gold",
  APPROVED: "green",
  REJECTED: "red",
  CANCELLED: "default",
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const SocietyAmendmentDetailPage = () => {
  useDocumentTitle("Society Request Detail");
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fromList = searchParams.get("from") === "list";
  const listSocietyId = searchParams.get("entityId") ?? "";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentAmendment, amendmentStatus, amendmentError } = useAppSelector(
    (state) => state.adminServices
  );
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelRemarks, setCancelRemarks] = useState("");

  const currentUserId = Number(getPersistedUser()?.user?.id);
  const isMaker = currentAmendment?.requestedBy === currentUserId;
  const isPending = currentAmendment?.status === "PENDING";

  useEffect(() => {
    if (id) {
      dispatch(getAmendmentById(Number(id)));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (amendmentStatus === "loading") {
      message.loading({ content: "Loading", key: MESSAGE_KEY });
    } else if (amendmentStatus === "saving") {
      message.loading({ content: "Processing", key: MESSAGE_KEY });
    } else if (amendmentStatus === "success") {
      message.success({ content: "Request updated", key: MESSAGE_KEY });
      if (fromList) {
        navigate("/admin-services/society");
      } else {
        navigate(`/${routes.CLUSTER_SOCIETY.SOCIETY_AMENDMENTS}`);
      }
    } else if (amendmentStatus === "error" && amendmentError) {
      message.error({ content: amendmentError, key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }
  }, [amendmentStatus, amendmentError, navigate, fromList]);

  const handleApprove = () => {
    if (!currentAmendment) return;
    dispatch(approveSocietyAmendment({ id: currentAmendment.id }));
  };

  const handleReject = () => {
    if (!currentAmendment || !rejectRemarks.trim()) {
      message.error("Remarks are required to reject");
      return;
    }
    dispatch(rejectSocietyAmendment({ id: currentAmendment.id, remarks: rejectRemarks.trim() }));
    setRejectModalOpen(false);
  };

  const handleCancel = () => {
    if (!currentAmendment) return;
    dispatch(cancelSocietyAmendment({
      id: currentAmendment.id,
      remarks: cancelRemarks.trim() || undefined,
    }));
    setCancelModalOpen(false);
  };

  const changeColumns = [
    { title: "Field", dataIndex: "field", key: "field" },
    {
      title: "Old Value",
      dataIndex: "oldValue",
      key: "oldValue",
      render: (value: unknown) => formatValue(value),
    },
    {
      title: "New Value",
      dataIndex: "newValue",
      key: "newValue",
      render: (value: unknown) => formatValue(value),
    },
  ];

  return (
    <>
      <Row>
        <Col xs={24}>
          <AppPageHeader title={`Society Request #${id}`} />
        </Col>
      </Row>

      {currentAmendment && (
        <>
          <Descriptions bordered size="small" column={2} style={{ marginBottom: "1.5rem" }}>
            <Descriptions.Item label="Request ID">{currentAmendment.id}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColorMap[currentAmendment.status] ?? "default"}>
                {currentAmendment.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Request Type">{currentAmendment.requestType}</Descriptions.Item>
            <Descriptions.Item label="Society ID">{currentAmendment.entityId ?? "-"}</Descriptions.Item>
            <Descriptions.Item label="Requested By">{currentAmendment.requestedBy}</Descriptions.Item>
            <Descriptions.Item label="Requested At">
              {new Date(currentAmendment.requestedAt).toLocaleString()}
            </Descriptions.Item>
            {currentAmendment.metadata?.remarks && (
              <Descriptions.Item label="Review Remarks" span={2}>
                {currentAmendment.metadata.remarks}
              </Descriptions.Item>
            )}
          </Descriptions>

          {isPending && isMaker && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: "1rem" }}
              message="You submitted this request. You can cancel it while it is pending."
            />
          )}

          {isPending && !isMaker && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: "1rem" }}
              message="Review the changes below and approve or reject this request."
            />
          )}

          <Table
            columns={changeColumns}
            dataSource={(currentAmendment.changes ?? []).map((change, index) => ({
              ...change,
              key: `${change.field}-${index}`,
            }))}
            pagination={false}
            size="small"
            bordered
            style={{ marginBottom: "1.5rem" }}
          />

          <Row gutter={12}>
            {isPending && !isMaker && (
              <>
                <Col>
                  <Button type="primary" onClick={handleApprove} loading={amendmentStatus === "saving"}>
                    Approve
                  </Button>
                </Col>
                <Col>
                  <Button danger onClick={() => setRejectModalOpen(true)} loading={amendmentStatus === "saving"}>
                    Reject
                  </Button>
                </Col>
              </>
            )}
            {isPending && isMaker && (
              <Col>
                <Button danger onClick={() => setCancelModalOpen(true)} loading={amendmentStatus === "saving"}>
                  Cancel Request
                </Button>
              </Col>
            )}
            <Col>
              <Button
                onClick={() =>
                  fromList
                    ? navigate("/admin-services/society")
                    : navigate(`/${routes.CLUSTER_SOCIETY.SOCIETY_AMENDMENTS}`)
                }
              >
                {fromList ? "Back to Society List" : "Back to Requests"}
              </Button>
            </Col>
          </Row>
        </>
      )}

      <Modal
        title="Reject Request"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={handleReject}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setRejectModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              danger
              onClick={handleReject}
              loading={amendmentStatus === "saving"}
              disabled={!rejectRemarks.trim()}
            >
              Reject
            </Button>
          </div>
        }
      >
        <p>Remarks are required to reject this request.</p>
        <Input.TextArea
          rows={3}
          value={rejectRemarks}
          onChange={(e) => setRejectRemarks(e.target.value)}
          placeholder="Enter rejection remarks"
        />
      </Modal>

      <Modal
        title="Cancel Request"
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        onOk={handleCancel}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setCancelModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              danger
              onClick={handleCancel}
              loading={amendmentStatus === "saving"}
            >
              Cancel Request
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to cancel this request?</p>
        <Input.TextArea
          rows={3}
          value={cancelRemarks}
          onChange={(e) => setCancelRemarks(e.target.value)}
          placeholder="Optional remarks"
        />
      </Modal>
    </>
  );
};

export default SocietyAmendmentDetailPage;
